/**
 * useTabParam — Persist active tab in URL ?tab= search param.
 *
 * Drop-in replacement for useState(defaultTab):
 *   const [tab, setTab] = useTabParam('overview');
 *
 * On mount reads ?tab= from URL; falls back to defaultTab.
 * setTab updates both state and URL (replaces history entry).
 *
 * @since v2.338.0
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function useTabParam(defaultTab, paramName = 'tab') {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get(paramName) || defaultTab;

  const setTab = useCallback((next) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (next === defaultTab) {
        p.delete(paramName);
      } else {
        p.set(paramName, next);
      }
      return p;
    }, { replace: true });
  }, [defaultTab, paramName, setSearchParams]);

  return [tab, setTab];
}
